// Dashboard - Vue d'ensemble Espace Pro
// Connecté aux vraies APIs

import { useState, useEffect } from 'react';
import { Link, useOutletContext, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package, ShoppingCart, Truck, MessageSquare, Euro, TrendingUp,
  Eye, Users, Star, ArrowRight, Clock, CheckCircle2, AlertCircle,
  Plus, Zap, FileText, Wrench, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { sellerStatsAPI, sellerOrdersAPI, sellerProductsAPI } from '../../services/sellerAPI';

// Carte de statistique
function StatCard({ icon: Icon, label, value, trend, color, link }) {
  const navigate = useNavigate();
  
  return (
    <motion.div
      onClick={() => link && navigate(link)}
      whileHover={{ y: -2, boxShadow: '0 8px 30px rgba(0,0,0,0.1)' }}
      className="bg-white rounded-2xl p-5 border border-gray-100 cursor-pointer transition-all"
      style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon size={24} style={{ color }} />
        </div>
        {trend !== undefined && trend !== null && (
          <span className={`flex items-center gap-1 text-sm font-medium ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            <TrendingUp size={14} className={trend < 0 ? 'rotate-180' : ''} />
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </motion.div>
  );
}

// Commande récente
function RecentOrder({ order }) {
  const statusColors = {
    new: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Nouvelle' },
    confirmed: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Confirmée' },
    preparing: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'En préparation' },
    shipped: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Expédiée' },
    delivered: { bg: 'bg-green-100', text: 'text-green-700', label: 'Livrée' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Annulée' },
  };
  const status = statusColors[order.status] || statusColors.new;
  const firstItem = order.items?.[0];

  return (
    <Link 
      to={`/compte/commandes/${order.id}`}
      className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
    >
      <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
        {firstItem?.product_image ? (
          <img src={firstItem.product_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <Package size={20} className="text-gray-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">
          {firstItem?.product_name || 'Commande'}
          {order.items?.length > 1 && ` +${order.items.length - 1}`}
        </p>
        <p className="text-sm text-gray-500">{order.order_number} • {order.buyer_name}</p>
      </div>
      <div className="text-right">
        <p className="font-bold text-gray-900">{order.total?.toFixed(2)} €</p>
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
          {status.label}
        </span>
      </div>
    </Link>
  );
}

// Alerte stock
function StockAlert({ product }) {
  const isOutOfStock = product.stock <= 0;
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl ${isOutOfStock ? 'bg-red-50' : 'bg-yellow-50'}`}>
      <AlertCircle size={18} className={isOutOfStock ? 'text-red-500' : 'text-yellow-500'} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{product.title}</p>
        <p className={`text-xs ${isOutOfStock ? 'text-red-600' : 'text-yellow-600'}`}>
          {isOutOfStock ? 'Rupture de stock' : `Stock bas : ${product.stock} unités`}
        </p>
      </div>
      <Link 
        to={`/compte/produits/${product.id}`}
        className="text-xs font-medium text-gray-600 hover:text-gray-900"
      >
        Gérer
      </Link>
    </div>
  );
}

// Actions rapides
function QuickAction({ icon: Icon, label, description, color, onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-all text-left w-full"
    >
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </motion.button>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { seller } = useOutletContext() || {};
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      // Charger les stats
      const statsData = await sellerStatsAPI.get('month');
      setStats(statsData);

      // Charger les commandes récentes
      try {
        const orders = await sellerOrdersAPI.getAll(null, 5);
        setRecentOrders(orders);
      } catch (e) {
        console.log('No orders yet');
      }

      // Charger les produits avec stock bas
      try {
        const products = await sellerProductsAPI.getAll('active', null, 100);
        const lowStock = products.filter(p => p.stock <= 5);
        setLowStockProducts(lowStock.slice(0, 5));
      } catch (e) {
        console.log('No products yet');
      }
    } catch (e) {
      console.error('Error fetching dashboard data:', e);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
          <p className="text-gray-500">Bienvenue, {seller?.shop_name || 'Professionnel'}</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={ShoppingCart}
          label="Nouvelles commandes"
          value={stats?.orders?.new || 0}
          color="#F97316"
          link="/compte/commandes"
        />
        <StatCard
          icon={Package}
          label="Produits actifs"
          value={stats?.products?.active || 0}
          color="#10B981"
          link="/compte/produits"
        />
        <StatCard
          icon={Euro}
          label="Revenu du mois"
          value={`${(stats?.revenue?.total || 0).toFixed(0)} €`}
          color="#3B82F6"
          link="/compte/paiements"
        />
        <StatCard
          icon={Star}
          label="Note moyenne"
          value={stats?.rating?.toFixed(1) || '0.0'}
          color="#EAB308"
          link="/compte/avis"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Commandes récentes */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Commandes récentes</h2>
              <Link 
                to="/compte/commandes" 
                className="text-sm font-medium text-orange-600 hover:text-orange-700 flex items-center gap-1"
              >
                Voir tout <ArrowRight size={14} />
              </Link>
            </div>
            <div className="p-4">
              {recentOrders.length > 0 ? (
                <div className="space-y-3">
                  {recentOrders.map((order) => (
                    <RecentOrder key={order.id} order={order} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShoppingCart size={40} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">Aucune commande pour le moment</p>
                  <p className="text-sm text-gray-400">Tes commandes apparaîtront ici</p>
                </div>
              )}
            </div>
          </div>

          {/* Actions rapides */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Actions rapides</h2>
            </div>
            <div className="p-4 grid sm:grid-cols-2 gap-3">
              <QuickAction
                icon={Plus}
                label="Ajouter un produit"
                description="Nouveau produit à vendre"
                color="#10B981"
                onClick={() => navigate('/compte/produits/nouveau')}
              />
              <QuickAction
                icon={Wrench}
                label="Ajouter une prestation"
                description="Nouveau service artisan"
                color="#8B5CF6"
                onClick={() => navigate('/compte/prestations/nouveau')}
              />
              <QuickAction
                icon={FileText}
                label="Créer un devis"
                description="Devis pour un client"
                color="#6366F1"
                onClick={() => navigate('/compte/devis/nouveau')}
              />
              <QuickAction
                icon={Zap}
                label="Créer une vente flash"
                description="Promotion limitée"
                color="#EF4444"
                onClick={() => navigate('/compte/ventes-flash/nouveau')}
              />
            </div>
          </div>
        </div>

        {/* Colonne latérale */}
        <div className="space-y-6">
          {/* Alertes stock */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Alertes stock</h2>
              {lowStockProducts.length > 0 && (
                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                  {lowStockProducts.length}
                </span>
              )}
            </div>
            <div className="p-4">
              {lowStockProducts.length > 0 ? (
                <div className="space-y-2">
                  {lowStockProducts.map((product) => (
                    <StockAlert key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle2 size={32} className="mx-auto text-green-500 mb-2" />
                  <p className="text-sm text-gray-500">Tous les stocks sont OK</p>
                </div>
              )}
            </div>
          </div>

          {/* Résumé du mois */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
            <h2 className="font-bold mb-4">Résumé du mois</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-white/80">Commandes</span>
                <span className="font-bold">{stats?.orders?.total || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/80">En préparation</span>
                <span className="font-bold">{stats?.orders?.preparing || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/80">Expédiées</span>
                <span className="font-bold">{stats?.orders?.shipped || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/80">Livrées</span>
                <span className="font-bold">{stats?.orders?.completed || 0}</span>
              </div>
              <div className="pt-3 border-t border-white/20 flex items-center justify-between">
                <span className="text-white/80">Revenu total</span>
                <span className="text-2xl font-bold">{(stats?.revenue?.total || 0).toFixed(2)} €</span>
              </div>
            </div>
          </div>

          {/* Messages non lus */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Messages</h2>
              {stats?.communication?.unread_messages > 0 && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                  {stats.communication.unread_messages} non lus
                </span>
              )}
            </div>
            <div className="p-4">
              <Link 
                to="/compte/messages"
                className="flex items-center justify-center gap-2 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 font-medium transition-colors"
              >
                <MessageSquare size={18} />
                Voir les messages
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
